// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import FilterItem from '../FilterItem'
import './upload-queue-filter.less'
import { Button, Checkbox, DatePicker, Input, Select, Spin, Tooltip } from 'antd'
import { CloseOutlined, SearchOutlined, UndoOutlined, UserSwitchOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { debounce } from 'lodash'
import moment from 'moment'

import { uploadTypeDictionary, platFormDictionary, jobStatusDictionary, deviceTypeDictionary } from 'constants/job'

function DebounceSelect ({ fetchOptions, debounceTimeout = 800, ...props }) {
  const [fetching, setFetching] = useState(false)
  const [options, setOptions] = useState([])
  const fetchRef = useRef(0)
  const debounceFetcher = useMemo(() => {
    const loadOptions = value => {
      fetchRef.current += 1
      const fetchId = fetchRef.current
      setOptions([])
      setFetching(true)
      fetchOptions(value).then(newOptions => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return
        }

        setOptions(newOptions)
        setFetching(false)
      })
    }

    return debounce(loadOptions, debounceTimeout)
  }, [fetchOptions, debounceTimeout])
  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size='small' /> : null}
      {...props}
      options={options}
    />
  )
}
const FormsFactory = props => {
  const { type, formProps = {} } = props
  switch (type) {
    case 'checkbox':
      return <Checkbox.Group {...formProps} />
    case 'timeRange':
      return <DatePicker.RangePicker size={'small'} {...formProps} />
    case 'text':
      return <Input {...formProps} />
    case 'select':
      return (
        <DebounceSelect
          style={{ width: '100%' }}
          size={'small'}
          showSearch
          placeholder={'Search by name'}
          filterOption={false}
          labelInValue
          fetchOptions={() => {
            return Promise.resolve([{ label: '123', value: '123' }])
          }}
          suffixIcon={<SearchOutlined />}
          {...formProps}
        />
      )
    default:
      return <span>please check the type</span>
  }
}
const UploadQueueFilter = props => {
  const {
    filters: { creator, jobType, deviceType, jobName, creatorPlatform },
    userInfo,
    currentCity
  } = useSelector(state => ({ ...state.jobList, currentCity: state.city.current, userInfo: state.auth.userInfo }))
  const [localFilters, setLocalFilters] = useState({
    creator,
    jobName,
    jobType,
    deviceType,
    creatorPlatform,
    status: [],
    timeRange: []
  })
  useEffect(() => {
    setLocalFilters({
      creator: userInfo.username,
      jobName: '',
      jobType: undefined,
      deviceType: undefined,
      creatorPlatform: [],
      status: [],
      timeRange: []
    })
  }, [currentCity, userInfo])
  const { className, onFilterChange, onChange, stage } = props
  const filterOptions = [
    {
      name: 'Creator',
      type: 'text',
      defaultCollapsed: false,
      stages: ['0', '1', '2'],
      formProps: {
        // value: creator,
        value: localFilters.creator,
        defaultValue: creator,
        placeholder: 'search by name',
        allowClear: true,
        size: 'small',
        onChange: e => {
          setLocalFilters({ ...localFilters, creator: e.target.value })
          const inputValue = e.target.value.trim()
          const creator = inputValue === '' ? [] : [inputValue]
          onChange('creator', creator)
        },
        suffix: (
          <Tooltip title={'Only show tasks created by me'}>
            <UserSwitchOutlined
              onClick={() => {
                setLocalFilters({ ...localFilters, creator: userInfo.username })
                onChange('creator', [userInfo.username])
              }}
            />
          </Tooltip>
        )
      }
    },
    {
      name: 'Job Name',
      type: 'text',
      stages: ['0', '1', '2'],
      defaultCollapsed: false,
      formProps: {
        // value: jobName,
        value: localFilters.jobName,
        defaultValue: jobName,
        size: 'small',
        allowClear: true,
        placeholder: 'Search by job name',
        onChange: e => {
          setLocalFilters({ ...localFilters, jobName: e.target.value })
          onChange('jobName', e.target.value)
        }
      }
    },
    {
      name: 'Job Date',
      type: 'timeRange',
      stages: ['0', '1', '2'],
      defaultCollapsed: false,
      formProps: {
        value: localFilters.timeRange,
        onChange: e => {
          setLocalFilters({ ...localFilters, timeRange: e })
          onChange(['startDate', 'endDate'], e ? e.map(item => moment(item).format('YYYY-MM-DD')) : [])
        }
      }
    },
    {
      name: 'Device Type',
      type: 'checkbox',
      stages: ['0', '1', '2'],
      defaultCollapsed: false,
      formProps: {
        defaultValue: deviceType,
        value: localFilters.deviceType,
        options: [...deviceTypeDictionary],
        className: 'checkbox',
        onChange: e => {
          setLocalFilters({ ...localFilters, deviceType: e })
          onChange('deviceType', e)
        }
      }
    },
    {
      name: 'Upload Type',
      type: 'checkbox',
      stages: ['0', '1', '2'],
      defaultCollapsed: false,
      formProps: {
        defaultValue: jobType,
        value: localFilters.jobType,
        options: [...uploadTypeDictionary],
        className: 'checkbox',
        onChange: e => {
          setLocalFilters({ ...localFilters, jobType: e })
          onChange('jobType', e)
        }
      }
    },
    {
      name: 'Overall Status',
      type: 'checkbox',
      stages: ['2'],
      defaultCollapsed: false,
      formProps: {
        options: [...jobStatusDictionary],
        value: localFilters.status,
        defaultValue: [],
        className: 'checkbox',
        onChange: e => {
          const status =  e.length > 0
            ? e.reduce((prev, cur) => [...prev, ...cur], [])
            : jobStatusDictionary
              .map(item => item.value)
              .reduce((prev, cur)=>[...prev,...cur],[])
          setLocalFilters({ ...localFilters, status: e })
          onChange('status', status)
        }
      }
    },
    {
      name: 'Platform',
      type: 'checkbox',
      stages: ['0', '1', '2'],
      defaultCollapsed: false,
      formProps: {
        value: localFilters.creatorPlatform,
        defaultValue: [creatorPlatform],
        options: [...platFormDictionary],
        className: 'checkbox',
        onChange: e => {
          setLocalFilters({ ...localFilters, creatorPlatform: e })
          const platForm = e.length === 1 ? e[0] : undefined
          onChange('creatorPlatform', platForm)
        }
      }
    }
  ].filter(item => item.stages && item.stages.includes(stage))
  const containerClass = classNames(['filter-container', className])
  const onClose = () => {
    if (onFilterChange) {
      onFilterChange(false)
    }
  }
  return (
    <div className={containerClass}>
      <div className='title'>
        Filter
        <Button type='text' size='small' onClick={onClose}>
          <CloseOutlined />
        </Button>
      </div>
      {filterOptions.map(item => (
        <FilterItem
          key={item.name}
          title={item.name}
          className={'filter-item'}
          defaultCollapsed={item.defaultCollapsed}
        >
          <FormsFactory type={item.type} formProps={item.formProps} />
        </FilterItem>
      ))}
    </div>
  )
}

export { UploadQueueFilter }
